import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { AuthProvider, useAuth } from "./components/AuthContext";
import { AdminProvider } from "./contexts/AdminContext";
import { CartProvider } from "./contexts/CartContext";
import { requestNotificationPermission, onMessageListener } from './firebase/config';
import { saveUserToken } from './services/notificationService';

// Components imports
import HomePage from "./pages/HomePage";
import ProducersPage from "./pages/ProducersPage";
import ProjectTimeline from "./pages/ProjectTimeline";
import NewsPage from "./pages/NewsPage";
import ArticlePage from "./pages/ArticlePage";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import TopBar from "./components/admin/TopBar";
import FarmerSignup from "./components/FarmerRegistration";
import FarmerRegistration from "./components/FarmerRegistration";
import FarmerLogin from "./components/FarmerLogin";
import BuyerDashboard from "./components/BuyerDashboard";
import AdminLogin from "./components/AdminLogin";
import AdminRegistration from "./components/AdminRegistration";
import AdminRecovery from "./components/AdminRecovery";
import AdminDashboard from "./components/AdminDashboard";
import EmailVerification from "./pages/EmailVerification";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";
import FarmerDashboard from './components/FarmerDashboard';
import SalesPage from "./pages/SalesPage";
import MarketplacePage from "./pages/MarketplacePage";

// Styles imports
import './index.css';
import './App.css';
import './styles/Form.css';
import './styles/Loader.css';
import theme from "./theme";

const AdminLayout = ({ children }) => {
  const { currentUser, userRole, logout } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'PDG';

  return (
    <div className="flex flex-col min-h-screen">
      {isAdmin && <TopBar activeSection="" setActiveSection={() => {}} handleLogout={logout} />}
      <main className={`flex-grow ${isAdmin ? 'pt-16' : ''}}`}>
        {children}
      </main>
    </div>
  );
};

// Supprimer ces imports car ils sont déjà déclarés en haut du fichier
// import { requestNotificationPermission, onMessageListener } from './firebase/config';
// import { saveUserToken } from './services/notificationService';

// Dans le composant AppContent
const AppContent = () => {
  const location = useLocation();
  const { currentUser, userRole } = useAuth();
  
  // Initialiser FCM
  useEffect(() => {
    const initializeFCM = async () => {
      if (currentUser) {
        try {
          const token = await requestNotificationPermission();
          if (token) {
            // Enregistrer le token dans Firestore
            await saveUserToken(currentUser.uid, userRole, token);
          }
        } catch (error) {
          console.error('Erreur lors de l\'initialisation de FCM:', error);
        }
      }
    };
    
    initializeFCM();
    
    // Écouter les messages FCM
    const unsubscribe = onMessageListener().then((payload) => {
      console.log('Message reçu:', payload);
      // Vous pouvez afficher une notification ici si nécessaire
    }).catch((err) => console.error('Erreur lors de la réception du message:', err));
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [currentUser, userRole]);
  
  // Déterminer si nous sommes sur une route admin
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  // Déterminer si nous sommes sur les pages de login/register admin
  const isAdminLoginOrRegister = location.pathname === '/admin-login' || location.pathname === '/admin-register';
  
  // Afficher Navbar et Footer sur les pages admin-login et admin-register si non connecté
  const showNavbarAndFooter = !isAdminRoute || (isAdminLoginOrRegister && !currentUser);

  const content = (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/producers" element={<ProducersPage />} />
      <Route path="/register" element={<FarmerRegistration />} />
      <Route path="/email-verification" element={<EmailVerification />} />
      <Route path="/projects" element={<ProjectTimeline />} />
      <Route path="/news" element={<NewsPage />} />
      <Route path="/news/:articleId" element={<ArticlePage />} />
      <Route path="/FarmerSignup" element={<FarmerSignup />} />
      <Route path="/farmer-login" element={<FarmerLogin />} />
      <Route path="/buyer-dashboard" element={<BuyerDashboard />} />
      <Route path="/admin-login" element={<AdminLayout><AdminLogin /></AdminLayout>} />
      <Route path="/admin-register" element={<AdminLayout><AdminRegistration /></AdminLayout>} />
      <Route path="/admin-recovery" element={<AdminLayout><AdminRecovery /></AdminLayout>} />
      <Route path="/admin-dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      <Route path="/farmer-dashboard" element={<ProtectedRoute><FarmerDashboard /></ProtectedRoute>} />
      <Route path="/sales" element={<SalesPage />} />
      <Route path="/centrale-achat" element={<MarketplacePage />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
    </Routes>
  );

  return (
    <div id="root">
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div className="flex flex-col min-h-screen">
          {showNavbarAndFooter && <Navbar />}
          <main className="flex-grow">
            {content}
          </main>
          {showNavbarAndFooter && <Footer />}
        </div>
      </ThemeProvider>
    </div>
  );

};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AdminProvider>
          <CartProvider>
            <Router>
              <AppContent />
            </Router>
          </CartProvider>
        </AdminProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;